// @ts-nocheck
/**
 * initRestore.ts
 * addLayers()のフェーズ別処理を分離
 * - restoreUserFiles: SIMA/KML/GeoJSON/DXF/GPX の復元
 * - restoreUploadedAssets: アップロードベクター/画像の復元
 */

import {
  addImageLayer,
  addImageLayerJpg,
  addImageLayerPng,
  addTileLayer,
  geojsonAddLayer,
  simaToGeoJSON,
  dxfToGeoJSON,
} from '@/js/downLoad'
import { gpx, kml } from '@tmcw/togeojson'
import DxfParser from 'dxf-parser'
import JSZip from 'jszip'

/**
 * ファイルをfetchしてFileオブジェクトとして返す
 */
async function fetchFile(url: string): Promise<File | undefined> {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`HTTPエラー! ステータス: ${response.status}`)
    }
    const blob = await response.blob()
    return new File([blob], 'downloaded_file', { type: blob.type })
  } catch (error) {
    console.error('ファイルの取得中にエラーが発生しました:', error)
  }
}

/**
 * SIMA/KML/GeoJSON/DXF/GPX を地図に復元
 * enginePhase !== 'init' で呼ばれる
 */
export function restoreUserFiles(map: unknown, store: unknown): void {
  // SIMA レイヤー削除→再追加
  if (map.getLayer('sima-layer')) {
    map.removeLayer('sima-layer')
    map.removeLayer('sima-borders')
    map.removeLayer('sima-label')
    map.removeLayer('sima-points')
    map.removeLayer('sima-label-point')
    map.removeLayer('sima-polygon-points')
    if (map.getLayer('sima-Vertex')) map.removeLayer('sima-Vertex')
    if (map.getLayer('sima-label-h')) map.removeLayer('sima-label-h')
  }
  if (store.state.simaText) {
    const parsed = JSON.parse(store.state.simaText)
    store.state.simaOpacity = parsed.opacity
    simaToGeoJSON(parsed.text, map, parsed.zahyokei, false)
    store.state.snackbar = true
    store.state.simaFire = !store.state.simaFire
  }

  // KML
  if (store.state.kmlText) {
    const parser = new DOMParser()
    const kmlData = parser.parseFromString(store.state.kmlText, 'text/xml')
    const geojson = kml(kmlData)
    geojsonAddLayer(map, geojson, false, 'kml')
  }

  // GeoJSON
  if (store.state.geojsonText) {
    const geojson = JSON.parse(store.state.geojsonText)
    geojsonAddLayer(map, geojson, false, 'geojson')
  }

  // DXF
  if (store.state.dxfText) {
    const parser = new DxfParser()
    store.state.zahyokei = store.state.dxfText.zahyokei
    const dxf = parser.parseSync(store.state.dxfText.text)
    const geojson = dxfToGeoJSON(dxf)
    geojsonAddLayer(map, geojson, false, 'dxf')
  }

  // GPX
  if (store.state.gpxText) {
    const parser = new DOMParser()
    const gpxDoc = parser.parseFromString(store.state.gpxText, 'application/xml')
    const geojson = gpx(gpxDoc)
    geojsonAddLayer(map, geojson, false, 'gpx')
  }
}

/**
 * アップロードベクター/画像を地図に復元
 * enginePhase === 'restoring' で呼ばれる
 */
export function restoreUploadedAssets(map: unknown, store: unknown): void {
  // アップロードベクター（KMZ等）
  if (store.state.uploadedVector) {
    const parsed = JSON.parse(store.state.uploadedVector)
    if (parsed.uid) {
      const vectorUrl = '/oh3/php/uploads/' + parsed.uid + '/' + parsed.image
      const extension = vectorUrl.split('.').pop()
      if (extension === 'kmz') {
        Promise.all([fetchFile(vectorUrl)]).then(files => {
          async function load() {
            const zip = await JSZip.loadAsync(files[0])
            const kmlFile = Object.keys(zip.files).find((name) => name.endsWith('.kml'))
            const kmlText = await zip.files[kmlFile].async('text')
            const parser = new DOMParser()
            const kmlData = parser.parseFromString(kmlText, 'text/xml')
            const geojson = kml(kmlData)
            geojsonAddLayer(map, geojson, false, 'kml')
          }
          load()
        })
      }
    }
  }

  // アップロード画像
  if (!store.state.uploadedImage) return

  if (map.getLayer('oh-geotiff-layer')) {
    map.removeLayer('oh-geotiff-layer')
  }

  const imgParsed = JSON.parse(store.state.uploadedImage)

  // タイルレイヤー
  if (imgParsed.tile) {
    addTileLayer(map)
  }

  // UID付き画像（新形式: /oh3/php/uploads/...）
  if (imgParsed.uid) {
    const imageUrl = '/oh3/php/uploads/' + imgParsed.uid + '/' + imgParsed.image
    const worldFileUrl = '/oh3/php/uploads/' + imgParsed.uid + '/' + imgParsed.worldFile
    const extension = imageUrl.split('.').pop()
    switch (extension) {
      case 'tif':
        if (worldFileUrl) {
          Promise.all([fetchFile(imageUrl), fetchFile(worldFileUrl)]).then(files => {
            addImageLayer(files[0], files[1], imgParsed.code, false)
          })
        } else {
          Promise.all([fetchFile(imageUrl)]).then(files => {
            addImageLayer(files[0], null, imgParsed.code, false)
          })
        }
        break
      case 'jpg':
        Promise.all([fetchFile(imageUrl), fetchFile(worldFileUrl)]).then(files => {
          addImageLayerJpg(files[0], files[1], imgParsed.code, false)
        })
        break
      case 'png':
        Promise.all([fetchFile(imageUrl), fetchFile(worldFileUrl)]).then(files => {
          addImageLayerPng(files[0], files[1], imgParsed.code, false)
        })
        break
    }
    return
  }

  // UID無し画像（旧形式: /oh3/php/image/...）
  if (!imgParsed.worldFile) {
    // ワールドファイルなし → GeoTIFF等
    const imageUrl = '/oh3/php/image/' + imgParsed.image
    Promise.all([fetchFile(imageUrl)]).then(files => {
      if (files.every(file => file)) {
        addImageLayer(files[0], null, imgParsed.code, false)
      }
    }).catch(error => {
      console.error('Promise.allでエラーが発生しました:', error)
    })
    return
  }

  const imageUrl = '/oh3/php/image/' + imgParsed.image
  const worldFileUrl = '/oh3/php/image/' + imgParsed.worldFile
  const worldExt = imgParsed.worldFile.split('.')[1]

  if (worldExt === 'tfw') {
    // TFW: TIFF + ワールドファイル
    Promise.all([fetchFile(imageUrl), fetchFile(worldFileUrl)]).then(files => {
      if (files.every(file => file)) {
        addImageLayer(files[0], files[1], imgParsed.code, false)
      }
    }).catch(error => {
      console.error('Promise.allでエラーが発生しました:', error)
    })
  } else if (worldExt === 'jgw') {
    // JGW: JPG + ワールドファイル
    Promise.all([fetchFile(imageUrl), fetchFile(worldFileUrl)]).then(files => {
      if (files.every(file => file)) {
        addImageLayerJpg(files[0], files[1], imgParsed.code, false)
      }
    }).catch(error => {
      console.error('Promise.allでエラーが発生しました:', error)
    })
  } else if (worldExt === 'pgw') {
    // PGW: PNG + ワールドファイル
    Promise.all([fetchFile(imageUrl), fetchFile(worldFileUrl)]).then(files => {
      if (files.every(file => file)) {
        addImageLayerPng(files[0], files[1], imgParsed.code, false)
      }
    }).catch(error => {
      console.error('Promise.allでエラーが発生しました:', error)
    })
  }
}
